import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {AlertService, UserService} from "../services";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

import {Router, ActivatedRoute} from '@angular/router';
import {catchError, debounceTime, first, map, startWith, switchMap} from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ComponentTitleService} from "../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";

import {locale as polish} from './i18n/pl';
import {SupplierService} from "../services/supplier.service";
import {SupplierOrderService} from "../services/supplierOrder.service";
import {Product} from "../models/product";
import {ProductService} from "../services/product.service";
import {UnitService} from "../services/unit.services";
import {VatRateService} from "../services/vatRate.service";
import {Unit} from "../models/unit";
import {VatRate} from "../models/vatRate";
import {Supplier} from "../models/supplier";
import { fuseAnimations } from '@fuse/animations';
import {MatSnackBar} from "@angular/material/snack-bar";
import {OrderService} from "../services/order.service";
import {User} from "../models";
import {VoucherTemplateService} from "../services/voucherTemplate.service";
import {VoucherTemplate} from "../models/voucherTemplate";
import {Country} from "../models/country";
import {CountryService} from "../services/country.service";
import {Items} from "../models/items";
import {Observable, of} from "rxjs";
import {CashReport} from "../cashReport/model/cashReport";

@Component({
    templateUrl: './newOrder.component.html',
    styleUrls: ['./newOrder.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})

export class NewOrderComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    type: string;
    productId: string;
    isAddMode: boolean;
    suppliers: Supplier[];
    dialogRef: any;
    products: Product[];
    units: Unit[];
    vatRates: VatRate[];
    customers: User[];
    workers: User[];
    voucherTemplates: VoucherTemplate[] = [];
    enabledVoucherSale: boolean;
    countries: Country[];
    paymentMethods: string[] = ['gotówka', 'karta'];
    filteredOptions: Observable<User[]>;
    activeCashReport: CashReport|null;

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private supplierService: SupplierService,
        private supplierOrderService: SupplierOrderService,
        private productService: ProductService,
        private unitService: UnitService,
        private vatRateService: VatRateService,
        private _matSnackBar: MatSnackBar,
        private orderService: OrderService,
        private userService: UserService,
        private voucherTemplateService: VoucherTemplateService,
        private countryService: CountryService,
    ) {
        this.translationLoader.loadTranslations(polish);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Orders');

        this.activeCashReport = this.route.snapshot.data.activeCashReport;

        if (!this.activeCashReport) {
            router.navigateByUrl('/cash-reports');
        }

        this.countryService.getAll().subscribe((res: any[]) => {
            this.countries = res;
        }, error => {
            this.alertService.error(error);
        })
    }

    ngOnInit() {
        this.enabledVoucherSale = false;
        this.productId = this.route.snapshot.params['productId'];
        this.type = this.route.snapshot.params['type'];

        this.isAddMode = true;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            customer: [''],
            description: [''],
            worker: ['', Validators.required],
            paymentMethod: ['', Validators.required],
            documentDate: [''],
            invoice: [''],
            nip: [''],
            companyName: [''],
            companyStreet: [''],
            companyPostCode: [''],
            companyCity: [''],
            companyCountry: [''],
            items: this.formBuilder.array([]),
            vouchers: this.formBuilder.array([]),
        });

        if (!this.isAddMode) {

        } else {
            this.addProductLine();
        }

        this.voucherTemplateService.getAll().pipe(first()).subscribe((res: VoucherTemplate[]) => {
                let templates = [];
                res.forEach(function(template: VoucherTemplate) {
                    if (template.services.length === 0 || template.nameForReceipt.length === 0) {
                        return;
                    }
                    templates.push(template);
                });

                this.voucherTemplates = templates;
            },
            error => {
                this.alertService.error(error);
            });

        this.userService.getAllWorkers().pipe(first()).subscribe((res: Items) => {
                this.workers = res.items;
            },
            error => {
                this.alertService.error(error);
            });

        this.productService.getAll().subscribe((res: Product[]) => {

            let products = [];
            res.forEach(function(product: Product) {
                if (product.nameForReceipt.length === 0) {
                    return;
                }
                products.push(product);
            });

            this.products = products;

            if (this.type === 'product' && this.productId) {
                for (let i = 0; i < this.products.length; i++) {
                    if (this.products[i].id != this.productId) {
                        continue;
                    }

                    this.items().at(0).get('product').setValue(this.productId);

                    if (typeof this.products[i].unit !== "undefined" && this.products[i].unit.id !== null) {
                        this.items().at(0).get('unit').setValue(this.products[i].unit.id);
                    }

                    if (typeof this.products[i].vatRate !== "undefined" && this.products[i].vatRate != null) {
                        this.items().at(0).get('vatRate').setValue(this.products[i].vatRate.vatValue);
                    }

                    if (typeof this.products[i].purchasePriceGross !== "undefined" && this.products[i].priceGross != null) {
                        this.items().at(0).get('priceGross').setValue(this.products[i].priceGross);
                    }

                    if (!this.items().at(0).get('quantity').value) {
                        this.items().at(0).get('quantity').setValue(1);
                    }

                    break;
                }
            }
        }, error => {
            this.alertService.error(error);
        });

        this.vatRateService.getAll().subscribe((res: VatRate[]) => {
            this.vatRates = res;
        }, error => {
            this.alertService.error(error);
        });

        this.unitService.getAll().subscribe((res: Unit[]) => {
            this.units = res;
        }, error => {
            this.alertService.error(error);
        });

        this.userService.getAllCustomers().subscribe((res: Items)=>{
            this.customers = res.items;
            this.filteredOptions = this.form.get('customer').valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(300),
                    switchMap(value => {
                        if (value !== '') {
                            return this.lookup(value);
                        } else {
                            return of(null);
                        }
                    })
                );
        }, error => {

        });
    }

    lookup(value: string): Observable<Items> {
        return this.userService.getAllCustomersBy(value).pipe(
            // map the item property of the github results as our return object
            map(results => results.items),
            // catch errors
            catchError(_ => {
                return of(null);
            })
        );
    }

    displayFn(userId: User): string {
        return (userId)? userId.firstName + ' ' + userId.lastName + '; ' + userId.phone: '';
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        // this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createOrder();
        } else {
            this.updateOrder();
        }
    }

    newProductLine(): FormGroup {
        return this.formBuilder.group({
            id: [''],
            product: ['', Validators.required],
            unit: ['', Validators.required],
            quantity: ['', Validators.required],
            vatRate: ['', Validators.required],
            // priceNet: ['', Validators.required],
            priceGross: ['', Validators.required],
        });
    }

    public addProductLine() {
        this.items().push(this.newProductLine());
    }

    public removeProductLine(lineIdx: number) {
        this.items().removeAt(lineIdx);
    }

    public items(): FormArray {
        return this.form.get('items') as FormArray;
    }

    newVoucherLine(): FormGroup {
        return this.formBuilder.group({
            id: [''],
            voucherTemplate: ['', Validators.required],
            quantity: ['', Validators.required],
            vatRate: ['', Validators.required],
            // priceNet: ['', Validators.required],
            priceGross: ['', Validators.required],
            description: [''],
            dedication: [''],
            owner: [''],
        });
    }

    public addVoucherLine() {
        this.vouchers().push(this.newVoucherLine());
    }

    public removeVoucherLine(lineIdx: number) {
        this.vouchers().removeAt(lineIdx);
    }

    public vouchers(): FormArray {
        return this.form.get('vouchers') as FormArray;
    }

    public enableVoucherSale() {
        this.enabledVoucherSale = true;
    }

    public onProductSelect(lineIdx: number, event: any) {
        for (let i = 0; i < this.products.length; i++) {
            if (this.products[i].id != event.value) {
                continue;
            }

            if (typeof this.products[i].unit !== "undefined" && this.products[i].unit.id !== null) {
                this.items().at(lineIdx).get('unit').setValue(this.products[i].unit.id);
            }

            if (typeof this.products[i].vatRate !== "undefined" && this.products[i].vatRate != null) {
                this.items().at(lineIdx).get('vatRate').setValue(this.products[i].vatRate.vatValue);
            }

            // if (typeof this.products[i].purchasePriceNet !== "undefined" && this.products[i].priceNet != null) {
            //     this.items().at(lineIdx).get('priceNet').setValue(this.products[i].priceNet);
            // }

            if (typeof this.products[i].priceGross !== "undefined" && this.products[i].priceGross != null) {
                this.items().at(lineIdx).get('priceGross').setValue(this.products[i].priceGross);
            }

            if (!this.items().at(lineIdx).get('quantity').value) {
                this.items().at(lineIdx).get('quantity').setValue(1);
            }

            break;
        }
    }

    public onVoucherSelect(lineIdx: number, event: any) {
        for (let i = 0; i < this.voucherTemplates.length; i++) {
            if (this.voucherTemplates[i].id != event.value) {
                continue;
            }

            if (typeof this.voucherTemplates[i].salePrice !== "undefined" && this.voucherTemplates[i].salePrice != null) {
                this.vouchers().at(lineIdx).get('priceGross').setValue(this.voucherTemplates[i].salePrice);
            }

            if (typeof this.voucherTemplates[i].vatRate !== "undefined" && this.voucherTemplates[i].vatRate != null) {
                this.vouchers().at(lineIdx).get('vatRate').setValue(this.voucherTemplates[i].vatRate.vatValue);
            }

            this.vouchers().at(lineIdx).get('quantity').setValue(1);

            break;
        }
    }

    public selectCustomer(event) {
        console.log(event);
    }

    public wantInvoice(event) {
        if (!event.checked) {
            this.f.nip.setValue("");
            this.f.companyName.setValue("");
            this.f.companyStreet.setValue("");
            this.f.companyPostCode.setValue("");
            this.f.companyCity.setValue("");
            this.f.companyCountry.setValue("");
        } else {
            let customerId = this.f.customer.value;

            for (let i = 0; i < this.customers.length; i++) {
                if (this.customers[i].id !== customerId) {
                    continue;
                }

                this.f.nip.setValue(this.customers[i].nip);
                this.f.companyName.setValue(this.customers[i].companyName);
                this.f.companyStreet.setValue(this.customers[i].companyStreet);
                this.f.companyPostCode.setValue(this.customers[i].companyPostCode);
                this.f.companyCity.setValue(this.customers[i].companyCity);
                this.f.companyCountry.setValue(this.customers[i].companyCountry);
            }
        }
    }

    public calculateLineValue(i: number) {
        return this.items().at(i).get('quantity').value * this.items().at(i).get('priceGross').value;
    }

    public calculateVoucherLineValue(i: number) {
        return this.vouchers().at(i).get('quantity').value * this.vouchers().at(i).get('priceGross').value;
    }

    private createOrder() {
        let order = this.form.value;

        if (!order.invoice) {
            order.nip = "";
            order.companyName = "";
            order.companyStreet = "";
            order.companyPostCode = "";
            order.companyCity = "";
            order.companyCountry = "";
        }

        if (order.customer !== null && typeof order.customer !== "undefined") {
            order.customer = order.customer.id;
        }

        if (order.items.length === 0 && order.vouchers.length === 0) {
            this.loading = false;
            this._matSnackBar.open('Order creation error - Positions are required', 'OK', {
                verticalPosition: 'top',
                duration: 5000,
                panelClass: ['snackbar-error']
            });

            return;
        }

        if (order.vouchers.length > 0 && (order.customer === null || typeof order.customer === "undefined")) {
            this.loading = false;
            this._matSnackBar.open('Order creation error - Customer is required', 'OK', {
                verticalPosition: 'top',
                duration: 5000,
                panelClass: ['snackbar-error']
            });

            return;
        }

        this.orderService.create(order)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Order added successfully');
                    this.router.navigate(['.', {relativeTo: this.route}]);

                    this._matSnackBar.open('Order created successfully', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                },
                error => {
                    this.loading = false;
                    this._matSnackBar.open('Order creation error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration: 5000,
                        panelClass: ['snackbar-error']
                    });
                });
    }

    private updateOrder() {
        this.orderService.update(this.id, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Update successful');
                    this.router.navigate(['..', {relativeTo: this.route}]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }
}
